import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import Backdrop from "@mui/material/Backdrop";
import { Blocks } from "react-loader-spinner";
import moment from "moment";
import Swal from "sweetalert2";
import FinancialHandler from "../../handlers/FinancialHandler";
import SelectFile from './SelectFile.js';

const ExcelStatementImport = () => {
  const location = useLocation();
  const [companyId, setCompanyId] = useState("NULL");
  const [loading, setLoading] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState([]);
  const [profitLoss, setProfitLoss] = useState([]);
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('user');
  const companyName = queryParams.get('name');

  const getCompanyId = async () => {
    const companyId = await FinancialHandler.fetchCompanyId({ userId, companyName });
    setCompanyId(companyId);
  };
  
  const sendData = async () => {
    if(!balanceSheet.length || !profitLoss.length) {
      Swal.fire("The data is not valid", "Please check your financial statements again", "warning");
      return;
    }
    setLoading(true);
    setCompanyId("NULL");

    let BS, PL, earlist, latest;

    if(new Date(balanceSheet[0].fromDateReport) < new Date(balanceSheet[balanceSheet.length - 1].fromDateReport)) BS = balanceSheet.reverse();
    else BS = balanceSheet;
    if(new Date(profitLoss[0].fromDateReport) < new Date(profitLoss[profitLoss.length - 1].fromDateReport)) PL = profitLoss.reverse();
    else PL = profitLoss;
    if(new Date(BS[BS.length - 1].fromDateReport) > new Date(PL[PL.length - 1].fromDateReport)) earlist = BS[BS.length - 1].fromDateReport;
    else earlist = PL[PL.length - 1].fromDateReport;
    if(new Date(BS[0].toDateReport) < new Date(PL[0].toDateReport)) latest = BS[0].toDateReport;
    else latest = PL[0].toDateReport;

    const payload = {
      companyId: companyId,
      accountingData: {
        balanceSheet: BS,
        profitLoss: PL,
      },
      otherData: {
        status: "COMPLETED",
        companyInfo: {
          earlistDate: earlist,
          latestDate: latest,
          source: "Excel"
        }
      }
    }
    try {
      await FinancialHandler.saveCompanyData(payload);
      Swal.fire("Completed!", "Your company data is saved successfully", "success");
    } catch (error) {
    }
    
    // setLoading(false);
    getCompanyId();
  }

  const stringToHash = (string) => {
    return string.split('').reduce((hash, char) => {
      return char.charCodeAt(0) + (hash << 6) + (hash << 16) - hash;
    }, 0);
  }

  const processDateArray = (dateArray) => {
    ////this function handles dateArray and returns yearly related results for processBS and processPL

    //pre-process the dateArray to handle various input cases
    dateArray.forEach((date, idx, array)  => {
      if(!idx) return;  //nothing to do with first element
      if(Number.isInteger(Number(date))) {      //when it is integer number
        if(date < 2500) array[idx] = "Dec " + date;   //convert to date when it is just number format of the year in yearly sheet
        else if(date > 25500) array[idx] = new Date((date - 25569) * 86400000);   //convert to JS date if it is Excel date(number)
      }
    });

    //check if the statement is yearly, comparing the difference between two dates with 70 days
    const isYearly = Math.abs(new Date(dateArray[1]) - new Date(dateArray[2])) / 86400000 > 70 ? true : false;
    let monthsArray = [];   //it saves the number of months between dates when it is yearly array. first element is meaningless

    if(isYearly) {
      //convert the dateArray as monthly
      let newArray = [dateArray[0]];
      const isReversed = (new Date(dateArray[1]) - new Date(dateArray[2])) > 0;

      monthsArray = dateArray.map((date, index, array) => {
        //get interval array for yearly data handling
        if(!index) return 0;
        if(isReversed) {
          if(index === array.length - 1) return 12;  //in date decendant array, the last year has 12 months
          return moment.utc(date).diff(moment.utc(array[index + 1]), 'months', false);
        }
        else {
          if(index === 1) return 12;  //in date accendant array, the first year has 12 months
          return moment.utc(date).diff(moment.utc(array[index - 1]), 'months', false);
        }
      })

      dateArray.map((date, index) => { 
        for(let i = 0; i < monthsArray[index]; i++) {
          //handle the reversed array
          newArray.push(moment.utc(date).clone()
            .add({ months: isReversed ? -i : - monthsArray[index] + i + 1 }).format("YYYY-MM-DD"));
        }
      });
      newArray.forEach((data, idx) => dateArray[idx] = data);   //update dateArray as newArray
    }

    return [isYearly, monthsArray];
  }

  const processBalanceSheet = (data) => {
    const dateArray = data[0];
    const length = dateArray.length;
    const nameArray = ["current assets", "fixed assets", "current liabilities", "long-term liabilities", "equity"];
    let rawArray = [[], [], [], [], []];
    let resultArray = [];
    let flagIndex = -1;

    const [isYearly, monthsArray] = processDateArray(dateArray);

    //get rawArray for each categories
    data.forEach(items => {
      const itemName = items[0] == null ? "" : items[0].toLowerCase();   //items[0] is the category / account name
      if(itemName.includes("total") || itemName == "") flagIndex = -1;    //end if it finds total line or blank line
      if(flagIndex >= 0) {
        for(let i = 1; i < length; i++) if(!(i in items)) items[i] = 0;
        if(isYearly) {
          //convert the array as monthly. Balance sheet is not monthly distributed but the same for 12 months
          let newItems = [items[0]];
          items.map((val, idx) => { for(let i = 0; i < monthsArray[idx]; i++) newItems.push(val) });
          rawArray[flagIndex].push(newItems);
        }
        else rawArray[flagIndex].push(items);
      }
      if(nameArray.includes(itemName)) flagIndex = nameArray.indexOf(itemName);   //prepare if it finds category line
    });

    //iterate according to the dates
    dateArray.map((date, index) => {
      if(!index) return;  //the first element is just the names
      resultArray.push({
        date: moment.utc(date).clone().add({ months: 1 }).add({ days: -1 }).format("YYYY-MM-DD"),
        fromDateReport: moment.utc(date).format("YYYY-MM-01"),
        toDateReport: moment.utc(date).clone().add({ months: 1 }).add({ days: -1 }).format("YYYY-MM-DD"),
        assets: {
          name: "Assets",
          items: [
            {
              name: "Fixed Assets",
              items: rawArray[1].map(arr => ({
                accountId: stringToHash(arr[0]),
                name: arr[0],
                value: Number(arr[index]) || 0,
              }))
            },
            {
              name: "Current Assets",
              items: rawArray[0].map(arr => ({
                accountId: stringToHash(arr[0]),
                name: arr[0],
                value: Number(arr[index]) || 0,
              }))
            },
          ]
        },
        liabilities: {
          name: "Liabilities",
          items: [
            {
              name: "Current Liabilities",
              items: rawArray[2].map(arr => ({
                accountId: stringToHash(arr[0]),
                name: arr[0],
                value: Number(arr[index]) || 0,
              }))
            },
            {
              name: "Long Term Liability",
              items: rawArray[3].map(arr => ({
                accountId: stringToHash(arr[0]),
                name: arr[0],
                value: Number(arr[index]) || 0,
              }))
            },
          ]
        },
        equity: {
          name: "Equity",
          items: rawArray[4].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        }
      })
    })

    setBalanceSheet(resultArray);
  };

  const processProfitLoss = (data) => {
    const dateArray = data[0];
    const length = dateArray.length;
    const nameArray = ["income", "cost of sales", "expenses", "other income", "other expenses"];
    let rawArray = [[], [], [], [], []];
    let resultArray = [];
    let flagIndex = -1;

    const [isYearly, monthsArray] = processDateArray(dateArray);

    //get rawArray for each categories
    data.forEach(items => {
      const itemName = items[0] == null ? "" : items[0].toLowerCase();   //items[0] is the category / account name
      if(itemName.includes("total") || itemName == "") flagIndex = -1;    //end if it finds total line or blank line
      if(flagIndex >= 0) {
        for(let i = 1; i < length; i++) if(!(i in items)) items[i] = 0;
        if(isYearly) {
          //convert the array as monthly. P n L is monthly distributed by the number of months for that year
          let newItems = [items[0]];
          items.map((val, idx) => { for(let i = 0; i < monthsArray[idx]; i++) newItems.push(val / monthsArray[idx]) });
          rawArray[flagIndex].push(newItems);
        }
        else rawArray[flagIndex].push(items);
      }
      if(nameArray.includes(itemName)) flagIndex = nameArray.indexOf(itemName);   //prepare if it finds category line
    });

    //iterate according to the dates
    dateArray.map((date, index) => {
      if(!index) return;  //the first element is just the names
      resultArray.push({
        fromDateReport: moment.utc(date).format("YYYY-MM-01"),
        toDateReport: moment.utc(date).clone().add({ months: 1 }).add({ days: -1 }).format("YYYY-MM-DD"),
        costOfSales: {
          name: "Cost of Sales",
          items: rawArray[1].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        },
        expenses: {
          name: "Expenses",
          items: rawArray[2].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        },
        income: {
          name: "Income",
          items: rawArray[0].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        },
        otherExpenses: {
          name: "Other Expenses",
          items: rawArray[4].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        },
        otherIncome: {
          name: "Other Income",
          items: rawArray[3].map(arr => ({
            accountId: stringToHash(arr[0]),
            name: arr[0],
            value: Number(arr[index]) || 0,
          }))
        },
      })
    })

    setProfitLoss(resultArray);
  };

  useEffect(() => {
    getCompanyId();
  }, []);

  return (
    <div>
      <Container maxWidth="md">
        {companyId === "NULL" ?
          <Backdrop
            sx={{ color: "fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={companyId === "NULL"}
          >
            <Blocks visible={true} height="80" width="80" />
          </Backdrop>
        : 
          (
            companyId === "" ?
              <Typography variant="h4" className="text-center text-indigo-600 font-bold pt-20">
                This link is invalid
              </Typography>
            :
              <>
                <SelectFile type="BS" data={balanceSheet} setData={processBalanceSheet} />
                <SelectFile type="PL" data={profitLoss} setData={processProfitLoss} />
                <div className="py-7 pr-20 flex justify-end">
                  <Button
                    onClick={sendData}
                    color="secondary"
                    endIcon={<SendIcon />}
                    loading={true}
                    loadingPosition="end"
                    variant="contained"
                  >
                    Send
                  </Button>
                </div>
              </>
          )
        }
      </Container>
    </div>
  );
};

export default ExcelStatementImport;